/**
 * A custom web component for rendering and managing a user manual UI.
 * Handles search, category selection, language switching, accordions, and custom scrollbars.
 * 
 * @element user-manual
 */

class UserManual extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

   /**
   * Initialize the component by setting up elements, event listeners, states, and swipers.
   * @private
   */
  init() {
    this.initializeElements();
    this.setupEventListeners();
    this.initializeDefaultStates();
    this.initializeSwipers();
  }

  /** Initialize DOM element references. */
  initializeElements() {
    this.languageDropdown = this.querySelector('#language-dropdown');
    this.selected = this.querySelector('#dropdown-selected');
    this.options = this.querySelector('#dropdown-options');
    this.optionsContent = this.querySelector('#dropdown-options-content');
    this.scrollbar = this.querySelector('#dropdown-scrollbar');
    this.scrollbarThumb = this.querySelector('#dropdown-scrollbar-thumb');
    this.optionElements = this.querySelectorAll('.user-manual-landing__dropdown-option');

    this.categories = this.querySelectorAll('.user-manual-landing__category');
    this.collectionsContainers = this.querySelectorAll('.user-manual-landing__category-collections-container');

    this.accordionHeaders = this.querySelectorAll('.user-manual-landing__category-collection-accordion-header');
    this.accordionCollections = this.querySelectorAll('.user-manual-landing__category-collection');
    this.collapseAllButtons = this.querySelectorAll('.user-manual-landing__collapse-all');

    this.productLinks = this.querySelectorAll('.user-manual-landing__category-product a');

    this.backToTopButton = this.querySelector('.user-manual-landing__back-to-top a');

    this.mainSearchInput = this.querySelector('.user-manual-landing__container .user-manual-landing__search-bar-input input');
    this.searchResultsInput = this.querySelector('.user-manual-landing__search-results .user-manual-landing__search-page-input input');
    this.mainContainer = this.querySelector('.user-manual-landing__container');
    this.searchResultsContainer = this.querySelector('.user-manual-landing__search-results');
    this.searchResultsContent = this.querySelector('.user-manual-landing__search-results-container');
    this.backToMainButton = this.querySelector('.user-manual-landing__back-to-main');
    this.mainSearchIcon = this.querySelector('.user-manual-landing__search-bar-input .user-manual-landing__search-bar-icon');
    this.searchResultsIcon = this.querySelector('.user-manual-landing__search-page-input .user-manual-landing__search-bar-icon');

    this.swiperContainers = this.querySelectorAll('.user-manual-landing__category-collection-swiper');
    this.swipers = [];
  }

  /** Attach all necessary event listeners. */
  setupEventListeners() {
    this.setupLanguageDropdown();
    this.setupCustomScrollbar();
    this.setupCategorySelection();
    this.setupAccordionToggle();
    this.setupCollapseAll();
    this.setupOutsideClickListener();
    this.setupSearchInput();
    this.setupBackToMain();
    this.setupBackToTop();
    this.setupResizeListener();
  }

  /** Setup the search input functionality. */
  setupSearchInput() {
    if (this.mainSearchInput) {
      this.mainSearchInput.addEventListener('keypress', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.showSearchResults(this.mainSearchInput.value);
        }
      });
    }

    if (this.mainSearchIcon && this.mainSearchInput) {
      this.mainSearchIcon.addEventListener('click', () => {
        this.showSearchResults(this.mainSearchInput.value);
      });
    }

    if (this.searchResultsInput) {
      this.searchResultsInput.addEventListener('keypress', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.filterSearchResults(this.searchResultsInput.value);
        }
      });
    }

    if (this.searchResultsIcon && this.searchResultsInput) {
      this.searchResultsIcon.addEventListener('click', () => {
        this.filterSearchResults(this.searchResultsInput.value);
      });
    }
  }

  /** Setup the back-to-main navigation. */
  setupBackToMain() {
    this.backToMainButton.addEventListener('click', () => {
      this.backToMainView();
    });
  }

  /** Setup the back-to-top button functionality. */
  setupBackToTop() {
    if (this.backToTopButton) {
      this.backToTopButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.scrollToTop();
      });
    }
  }

  /** Setup the language dropdown toggle and selection. */
  setupLanguageDropdown() {
    if (!this.selected || !this.optionElements.length) return;

    this.selected.addEventListener('click', () => {
      this.toggleDropdown();
    });

    this.optionElements.forEach(option => {
      option.addEventListener('click', (event) => {
        this.selectLanguageOption(event.target);
      });
    });
  }

  /** Initialize custom scrollbar. */
  setupCustomScrollbar() {
    if (!this.optionsContent || !this.scrollbar || !this.scrollbarThumb) return;

    this.isDragging = false;
    this.startY = 0;
    this.startScrollTop = 0;

    this.optionsContent.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.scrollContent(e.deltaY);
    });

    this.optionsContent.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      this.contentStartY = e.clientY;
      this.contentStartScrollTop = this.currentScrollTop || 0;
      this.isContentDragging = false;
    });

    this.optionsContent.addEventListener('pointermove', (e) => {
      const deltaY = Math.abs(this.contentStartY - e.clientY);

      // Only start dragging if moved more than 5 pixels
      if (deltaY > 5 && !this.isContentDragging) {
        this.isContentDragging = true;
        this.optionsContent.setPointerCapture(e.pointerId);
      }

      if (!this.isContentDragging) return;
      if (!this.optionsContent.hasPointerCapture(e.pointerId)) return;
      e.preventDefault();

      const scrollDeltaY = this.contentStartY - e.clientY;
      const maxScrollTop = this.optionsContent.scrollHeight - this.optionsContent.clientHeight;
      const newScrollTop = Math.max(0, Math.min(maxScrollTop, this.contentStartScrollTop + scrollDeltaY));

      this.setContentScrollTop(newScrollTop);
    });

    this.optionsContent.addEventListener('pointerup', (e) => {
      if (this.isContentDragging) {
        this.optionsContent.releasePointerCapture(e.pointerId);
        this.isContentDragging = false;
      }
    });

    this.scrollbarThumb.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.isDragging = true;
      this.startY = e.clientY;
      this.startScrollTop = this.currentScrollTop || 0;
      this.scrollbarThumb.setPointerCapture(e.pointerId);
      document.body.style.userSelect = 'none';
    });

    document.addEventListener('pointermove', (e) => {
      if (!this.isDragging) return;
      e.preventDefault();

      const deltaY = e.clientY - this.startY;
      this.handleScrollbarDrag(deltaY);
    });

    document.addEventListener('pointerup', () => {
      if (this.isDragging) {
        this.isDragging = false;
        document.body.style.userSelect = '';
      }
    });

    this.scrollbar.addEventListener('click', (e) => {
      if (e.target === this.scrollbarThumb) return;

      const rect = this.scrollbar.getBoundingClientRect();
      const clickY = e.clientY - rect.top;
      const thumbHeight = this.scrollbarThumb.clientHeight;
      const scrollbarHeight = this.scrollbar.clientHeight;
      const maxScrollTop = this.optionsContent.scrollHeight - this.optionsContent.clientHeight;

      const targetThumbTop = Math.max(0, Math.min(scrollbarHeight - thumbHeight, clickY - thumbHeight / 2));
      const scrollRatio = targetThumbTop / (scrollbarHeight - thumbHeight);
      const newScrollTop = scrollRatio * maxScrollTop;

      this.setContentScrollTop(newScrollTop);
    });
  }

  /**
   * Handle scrollbar dragging.
   * @param {number} deltaY - Vertical scroll delta.
   */
  handleScrollbarDrag(deltaY) {
    const scrollbarHeight = this.scrollbar.clientHeight;
    const thumbHeight = this.scrollbarThumb.clientHeight;
    const maxThumbTop = scrollbarHeight - thumbHeight;

    if (maxThumbTop <= 0) return;

    const scrollRatio = deltaY / maxThumbTop;
    const maxScrollTop = this.optionsContent.scrollHeight - this.optionsContent.clientHeight;
    const newScrollTop = Math.max(0, Math.min(maxScrollTop, this.startScrollTop + (scrollRatio * maxScrollTop)));

    this.setContentScrollTop(newScrollTop);
  }

  /**
   * Scroll the content area by a given amount.
   * @param {number} amount - The scroll amount in pixels.
   */
  scrollContent(amount) {
    const maxScrollTop = this.optionsContent.scrollHeight - this.optionsContent.clientHeight;
    const newScrollTop = Math.max(0, Math.min(maxScrollTop, (this.currentScrollTop || 0) + amount));
    this.setContentScrollTop(newScrollTop);
  }

  /**
   * Set the content scroll position.
   * @param {number} scrollTop - New scroll position.
   */
  setContentScrollTop(scrollTop) {
    this.currentScrollTop = scrollTop;
    this.optionsContent.style.transform = `translateY(-${scrollTop}px)`;
    this.updateScrollbarThumb();
  }

  /** Update the position of the scrollbar thumb. */
  updateScrollbarThumb() {
    if (!this.optionsContent || !this.scrollbar || !this.scrollbarThumb) return;

    const contentHeight = this.optionsContent.scrollHeight;
    const visibleHeight = this.optionsContent.clientHeight;
    const scrollbarHeight = this.scrollbar.clientHeight;

    if (contentHeight <= visibleHeight) {
      this.scrollbar.classList.remove('visible');
      return;
    } else {
      this.scrollbar.classList.add('visible');
    }

    const thumbHeight = Math.max(20, (visibleHeight / contentHeight) * scrollbarHeight);
    const maxScrollTop = contentHeight - visibleHeight;
    const scrollRatio = maxScrollTop > 0 ? (this.currentScrollTop || 0) / maxScrollTop : 0;
    const maxThumbTop = scrollbarHeight - thumbHeight;
    const thumbTop = scrollRatio * maxThumbTop;

    this.scrollbarThumb.style.height = `${thumbHeight}px`;
    this.scrollbarThumb.style.top = `${thumbTop}px`;
  }

  /** Setup category selection handlers. */
  setupCategorySelection() {
    this.categories.forEach(category => {
      category.addEventListener('click', () => {
        this.selectCategory(category);
      });
    });
  }

  /** Setup accordion toggle functionality. */
  setupAccordionToggle() {
    this.accordionHeaders.forEach(header => {
      header.addEventListener('click', () => {
        this.toggleAccordion(header);
      });
    });
  }

  /** Setup collapse-all functionality for accordions. */
  setupCollapseAll() {
    this.collapseAllButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.collapseAllCollections();
      });
    });
  }

  /** Add listener to handle clicks outside dropdowns/menus. */
  setupOutsideClickListener() {
    document.addEventListener('click', (event) => {
      if (this.languageDropdown && !this.languageDropdown.contains(event.target)) {
        this.closeDropdown();
      }
    });
  }

  /** Add resize listener to handle layout adjustments. */
  setupResizeListener() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.initializeSwipers();
      }, 250);
    });
  }

  /** Initialize default states for UI components. */
  initializeDefaultStates() {
    if (this.optionElements.length > 0) {
      this.optionElements[0].classList.add('selected');
    }

    this.showSelectedCategoryCollections(0);

    if (this.optionElements.length > 0) {
      const defaultLanguageValue = this.optionElements[0].getAttribute('data-value');
      this.updateProductLinks(defaultLanguageValue);
    }

    setTimeout(() => {
      this.initializeSwipers();
    }, 300);
  }

  /** Toggle the dropdown menu. */
  toggleDropdown() {
    if (!this.languageDropdown) return;
    const isOpening = !this.languageDropdown.classList.contains('open');
    this.languageDropdown.classList.toggle('open');

    if (isOpening) {
      setTimeout(() => {
        this.updateScrollbarThumb();
      }, 50);
    }
  }

  /** Close the dropdown menu. */
  closeDropdown() {
    if (!this.languageDropdown) return;
    this.languageDropdown.classList.remove('open');
    this.currentScrollTop = 0;
    if (this.optionsContent) {
      this.optionsContent.style.transform = 'translateY(0px)';
    }
  }

  /**
   * Handle selection of a language option.
   * @param {HTMLElement} option - The selected language option.
   */
  selectLanguageOption(option) {
    const value = option.getAttribute('data-value');
    const text = option.textContent.replace(/\s+/g, ' ').trim();
    const langCode = option.getAttribute('data-lang-code');
    if (langCode) {
      Weglot.switchTo(langCode);
    }

    const selectedSpan = this.selected.querySelector('span');
    if (selectedSpan) {
      selectedSpan.textContent = text;
    }

    this.optionElements.forEach(opt => opt.classList.remove('selected'));
    option.classList.add('selected');

    this.closeDropdown();

    this.languageDropdown.setAttribute('data-selected', value);

    this.updateProductLinks(value);

    const changeEvent = new CustomEvent('languageChanged', {
      detail: { value: value, text: text }
    });
    this.languageDropdown.dispatchEvent(changeEvent);
  }

  /**
   * Show search results for a given term.
   * @param {string} searchTerm - The search term.
   */
  showSearchResults(searchTerm) {
    const trimmedSearchTerm = searchTerm.trim();

    if (trimmedSearchTerm === '') {
      return;
    }

    if (this.mainContainer) {
      this.mainContainer.style.display = 'none';
    }
    if (this.searchResultsContainer) {
      this.searchResultsContainer.style.display = 'block';
    }

    if (this.searchResultsInput) {
      this.searchResultsInput.value = trimmedSearchTerm;
    }

    this.filterSearchResults(trimmedSearchTerm);
  }

  /**
   * Filter available search results.
   * @param {string} searchTerm - The search term to filter by.
   * @returns {Array<Object>} Filtered results.
   */
  filterSearchResults(searchTerm) {
    const trimmedSearchTerm = searchTerm.trim();

    if (trimmedSearchTerm === '') {
      this.displaySearchResults([], trimmedSearchTerm);
      return;
    }

    let enhancedSearchTerm = trimmedSearchTerm;
    if (!trimmedSearchTerm.toLowerCase().includes('manual')) {
      enhancedSearchTerm = `${trimmedSearchTerm} manual`;
    }

    const searchUrl = `${window.location.origin}/search?q=${encodeURIComponent(enhancedSearchTerm)}&options%5Bprefix%5D=last&type=page`;

    fetch(searchUrl)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const pages = [];

        const allCards = doc.querySelectorAll('.card--search, .search-result, .card, .page-card, [data-search-result]');

        allCards.forEach(card => {
          let href = null;
          let title = null;

          const mainLink = card.querySelector('a');
          if (mainLink) {
            href = mainLink.getAttribute('href');
            const titleEl = card.querySelector('h3, h2, .card__heading, .card__title, .search__result-title');
            title = titleEl ? titleEl.textContent.trim() : mainLink.textContent.trim();
          } else {
            const titleEl = card.querySelector('.card__text, h2, h3, .card__heading');
            if (titleEl) {
              title = titleEl.textContent.trim();
            }

            href = card.getAttribute('data-href') || card.getAttribute('data-url') || card.getAttribute('href');

            const parentLink = card.closest('a');
            if (parentLink) {
              href = parentLink.getAttribute('href');
            }

            if (!href && title && title.toLowerCase().includes('user manual')) {
              const titleParts = title.toLowerCase();
              const product = titleParts.match(/(w4 \w+|w4)/)?.[0]?.replace(' ', '-') || 'w4';
              const language = titleParts.match(/(us english|uk english|eu french|eu spanish|de german)/)?.[0]?.replace(' ', '-') || 'us-english';
              href = `/pages/${product}-user-manual-${language}`;
            }
          }

          if (href && title && title.length > 0) {
            pages.push({
              title: title,
              url: href.startsWith('http') ? href : window.location.origin + href
            });
          }
        });

        this.displaySearchResults(pages, trimmedSearchTerm);
      })
      .catch(error => {
        console.error('Search error:', error);
        this.displaySearchResults([], trimmedSearchTerm);
      });
  }

  /**
   * Display search results in the UI.
   * @param {Array<Object>} pages - Pages to display.
   * @param {string} searchTerm - The search term used.
   */
  displaySearchResults(pages, searchTerm) {
    if (!this.searchResultsContent) return;

    let resultsHTML = `<div class="user-manual-landing__search-results-header">`;

    if (pages.length > 0) {
      resultsHTML += `<p>Search Results for:</p><p class="user-manual-landing__search-results-header-search-term">"${searchTerm}"</p>`;
    } else {
      resultsHTML += `<p>No results found for "${searchTerm}"</p>`;
    }

    resultsHTML += `</div>`;

    if (pages.length > 0) {
      resultsHTML += `<div class="user-manual-landing__search-results-grid">`;

      pages.forEach(page => {
        resultsHTML += `
          <a href="${page.url}" class="user-manual-landing__search-result-product">
            <div class="user-manual-landing__search-result-product-title">${page.title}</div>
          </a>
        `;
      });

      resultsHTML += `</div>`;
    }
    this.searchResultsContent.innerHTML = resultsHTML;
  }

  /** Return to the main manual view. */
  backToMainView() {
    if (this.mainContainer) {
      this.mainContainer.style.display = 'block';
    }
    if (this.searchResultsContainer) {
      this.searchResultsContainer.style.display = 'none';
    }
  }

  /** Scroll smoothly to the top of the page. */
  scrollToTop() {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  /**
   * Update product links when language changes.
   * @param {string} selectedLanguage - The chosen language code.
   */
  updateProductLinks(selectedLanguage) {
    const cleanSelectedLanguage = selectedLanguage.replace(/\s+/g, ' ').trim();

    const allProductContainers = this.querySelectorAll('.user-manual-landing__category-product');
    allProductContainers.forEach(container => {
      container.classList.remove('hidden');
    });

    this.productLinks.forEach(link => {
      const userManualsData = link.getAttribute('data-user-manuals');
      const productContainer = link.closest('.user-manual-landing__category-product');

      if (userManualsData) {
        try {
          const userManuals = JSON.parse(userManualsData);

          const manual = userManuals.find(item => {
            const itemText = item.text ? item.text.replace(/\s+/g, ' ').trim() : '';
            return itemText === cleanSelectedLanguage;
          });

          if (manual && manual.url) {
            link.href = manual.url;
          } else {
            if (productContainer) {
              productContainer.classList.add('hidden');
            }
          }
        } catch (error) {
          if (productContainer) {
            productContainer.classList.add('hidden');
          }
        }
      } else {
        if (productContainer) {
          productContainer.classList.add('hidden');
        }
      }
    });

    this.reorganizeProductColumns();

    this.productLinks.forEach(link => {
      const userManualsData = link.getAttribute('data-user-manuals');
      if (userManualsData) {
        try {
          const userManuals = JSON.parse(userManualsData);
          const manual = userManuals.find(item => {
            const itemText = item.text ? item.text.replace(/\s+/g, ' ').trim() : '';
            return itemText === cleanSelectedLanguage;
          });

          if (manual && manual.url) {
            link.href = manual.url;
          }
        } catch (error) {
        }
      }
    });

    const selectedOption = this.optionElements ? Array.from(this.optionElements).find(opt =>
      opt.getAttribute('data-value') === cleanSelectedLanguage
    ) : null;
    const displayText = selectedOption ? selectedOption.textContent.replace(/\s+/g, ' ').trim() : cleanSelectedLanguage;

    setTimeout(() => {
      this.checkAndShowNoManualsMessages(displayText);
    }, 50);
  }

  /** Reorganize product columns layout. */
  reorganizeProductColumns() {
    const collections = this.querySelectorAll('.user-manual-landing__category-collection');

    collections.forEach(collection => {
      const swiperWrapper = collection.querySelector('.user-manual-landing__category-collection-swiper-wrapper');
      if (!swiperWrapper) return;

      const allProducts = collection.querySelectorAll('.user-manual-landing__category-product');

      const visibleProducts = Array.from(allProducts).filter(product =>
        !product.classList.contains('hidden')
      );
      const hiddenProducts = Array.from(allProducts).filter(product =>
        product.classList.contains('hidden')
      );

      const orderedProducts = [...visibleProducts, ...hiddenProducts];

      swiperWrapper.innerHTML = '';

      if (orderedProducts.length > 0) {
        let currentColumn = null;

        orderedProducts.forEach((product, index) => {
          if (index % 3 === 0) {
            currentColumn = document.createElement('div');
            currentColumn.className = 'user-manual-landing__category-collection-column swiper-slide';
            swiperWrapper.appendChild(currentColumn);
          }

          const productClone = product.cloneNode(true);
          currentColumn.appendChild(productClone);
        });
      }
    });

    this.productLinks = this.querySelectorAll('.user-manual-landing__category-product a');

    const collectionColumns = Array.from(this.querySelectorAll('.user-manual-landing__category-collection-column'))
    collectionColumns.forEach(c => {
      const currentColumnProducts = c.querySelectorAll('.user-manual-landing__category-product')
      const currentColumnHiddenProducts = c.querySelectorAll('.user-manual-landing__category-product.hidden')
      if(currentColumnProducts.length == currentColumnHiddenProducts.length) {
        c.classList.add('swiper-slide-hidden')
      } else {
        c.classList.remove('swiper-slide-hidden')
      }
    })
  }

  /**
   * Check and show "no manuals available" messages.
   * @param {string} selectedLanguage - The chosen language code.
   */
  checkAndShowNoManualsMessages(selectedLanguage) {
    const collections = this.querySelectorAll('.user-manual-landing__category-collection');

    collections.forEach(collection => {
      const productContainers = collection.querySelectorAll('.user-manual-landing__category-product');
      const noManualsMessage = collection.querySelector('.user-manual-landing__no-manuals-message');

      const allProductsHidden = Array.from(productContainers).every(product =>
        product.classList.contains('hidden')
      );

      const shouldShowMessage = (productContainers.length === 0) || (allProductsHidden && productContainers.length > 0);

      if (shouldShowMessage) {
        noManualsMessage.style.display = 'flex';
        collection.classList.add('has-message');
        collection.classList.remove('items-1', 'items-2');
        const languageSpan = noManualsMessage.querySelector('.selected-language-text');
        if (languageSpan) {
          languageSpan.textContent = selectedLanguage;
        }
      } else {
        noManualsMessage.style.display = 'none';
        collection.classList.remove('has-message');

        const visibleProducts = Array.from(productContainers).filter(product =>
          !product.classList.contains('hidden')
        );
        const visibleCount = visibleProducts.length;

        collection.classList.remove('items-1', 'items-2');

        if (visibleCount === 1) {
          collection.classList.add('items-1');
        } else if (visibleCount === 2) {
          collection.classList.add('items-2');
        }
      }
    });

    this.initializeSwipers();
  }

  /** Initialize swiper sliders/carousels. */
  initializeSwipers() {
    this.swipers.forEach(swiper => {
      if (swiper && swiper.destroy) {
        swiper.destroy(true, true);
      }
    });
    this.swipers = [];

    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.initializeSwipers(), 100);
      return;
    }

    this.swiperContainers.forEach(container => {
      const collection = container.closest('.user-manual-landing__category-collection');
      const isOpen = collection && collection.classList.contains('open');

      if (!isOpen) return;

      const columns = container.querySelectorAll('.user-manual-landing__category-collection-column');

      const columnsWithVisibleProducts = Array.from(columns).filter(column => {
        const products = column.querySelectorAll('.user-manual-landing__category-product');
        const visibleProducts = Array.from(products).filter(product =>
          !product.classList.contains('hidden')
        );
        return visibleProducts.length > 0;
      });

      const totalVisibleProducts = container.querySelectorAll('.user-manual-landing__category-product:not(.hidden)').length;
      if (totalVisibleProducts === 0) {
        return;
      }

      const isMobile = window.innerWidth <= 768;
      const maxVisibleColumns = isMobile ? 2 : 4;


      if (columnsWithVisibleProducts.length > maxVisibleColumns) {
        try {
          const swiper = new Swiper(container, {
            slidesPerView: isMobile ? 2 : 4,
            resistanceRatio: 0.85,
            watchOverflow: true,
            observer: true,
            observeParents: true,
            grabCursor: true,
            touchRatio: 1,
            touchAngle: 45,
            longSwipes: true,
            longSwipesRatio: 0.5,
            longSwipesMs: 300,
            pagination: {
              el: container.querySelector('.user-manual-landing__category-collection-swiper-pagination'),
              clickable: true,
              dynamicBullets: false,
            },
            breakpoints: {
              1024: {
                slidesPerView: 4,
              },
              769: {
                slidesPerView: 3,
              },
              550: {
                slidesPerView: 3,
              },
              320: {
                slidesPerView: 2,
              }
            }
          });

          this.swipers.push(swiper);
        } catch (error) {
          console.error('Error creating Swiper:', error);
        }
      }
    });
  }

  /**
   * Select a category and update UI.
   * @param {HTMLElement} selectedCategory - The selected category ID/name.
   */
  selectCategory(selectedCategory) {
    const selectedIndex = parseInt(selectedCategory.getAttribute('data-index'));

    this.categories.forEach(category => {
      category.classList.remove('selected');
    });
    selectedCategory.classList.add('selected');

    this.showSelectedCategoryCollections(selectedIndex);
  }

  /**
   * Show collections for the selected category.
   * @param {number} selectedIndex - Index of the category to display.
   */
  showSelectedCategoryCollections(selectedIndex) {
    this.collectionsContainers.forEach(container => {
      const containerIndex = parseInt(container.getAttribute('data-index'));
      if (containerIndex === selectedIndex) {
        container.style.display = 'block';
      } else {
        container.style.display = 'none';
      }
    });
  }

  /**
   * Toggle an accordion section open/close.
   * @param {HTMLElement} header - The accordion header element.
   */
  toggleAccordion(header) {
    const collection = header.nextElementSibling;
    if (collection && collection.classList.contains('user-manual-landing__category-collection')) {
      collection.classList.toggle('open');

      header.classList.toggle('open');

      setTimeout(() => {
        this.initializeSwipers();
      }, 300);
    }
  }

  /** Collapse all accordion collections. */
  collapseAllCollections() {
    this.isCollapsed = true;
    this.accordionCollections.forEach(collection => {
      if (collection.classList.contains('open')) {
        collection.classList.remove('open');
        this.isCollapsed = false;
      }
    });

    this.accordionHeaders.forEach(header => {
      header.classList.remove('open');
    });

    setTimeout(() => {
      this.initializeSwipers();
    }, 300);

    this.categorySection = this.querySelector('.user-manual-landing__categories-container');
    if (this.categorySection) {
      if (!this.isCollapsed) {
        window.scrollTo({
          top: this.categorySection.offsetTop - 140,
          behavior: 'smooth'
        });
      }
    }
  }
}

customElements.define('user-manual', UserManual);